/**
 * 하루 이용 횟수 정책 — **단일 출처**.
 *
 * 구간 구조 (회원·비회원 구분 없음)
 *   1) 무료 구간   하루 FREE_LIMIT회 — 광고 없음
 *   2) 광고 구간   그 뒤로는 **해석 1회마다 광고 1편** (DAILY_HARD_LIMIT회까지)
 *   3) 하루 상한   DAILY_HARD_LIMIT 도달 시 종료 (한국시간 자정 초기화)
 *
 * 2026-10-10 변경: 무료 3회(회원 5회) + 3회 중 1회 꼴 무작위 광고 → 무료 1회 + 1광고 1운세.
 * 로그인 보너스도 없앴다 — 로그인은 히스토리·닉네임 같은 편의 기능만 연다.
 *
 * 식별은 기기 쿠키(+회원이면 계정)로만 한다. "오늘 최초 접속"을 판단해 무료분을 주는 정도이고,
 * 무료분은 말 그대로 무료 제공분이라 완벽한 식별을 목표로 하지 않는다.
 *
 * ⚠️ 광고는 **자동화(봇) 방어 수단이 아니다.** 봇은 브라우저 없이 API를 직접 호출하므로
 *    광고 UI를 만나지 않는다. 실제 비용 방어선은 전역 일일 상한(LLM_DAILY_CAP)이다.
 */

/** 하루 무료 횟수 (광고 없음) — 회원·비회원 같다 */
export const FREE_LIMIT = 1;

/**
 * 광고 구간까지 포함한 1인 하루 상한.
 *
 * 20 → 50 (2026-08-28). 20회로는 미니앱 실기기 테스트가 하루를 못 버텼다.
 * 기기당 최대 원가는 50 × ₩2.1 ≈ ₩105이고, 실제 비용 방어선은 여전히
 * 전역 일일 상한(LLM_DAILY_CAP)이다 — doc/usage-limit.md
 */
export const DAILY_HARD_LIMIT = 50;

/**
 * 다음 해석에 광고가 필요한가 — 서버 차감과 화면 게이트가 함께 쓰는 **유일한 판정**.
 *
 * 무료분 뒤의 해석은 한 번에 광고 한 편이다. 무료분 이후 쓴 횟수(`used - FREE_LIMIT`)만큼은
 * 이미 광고를 봤어야 하고, **이번 몫까지 봤을 때만**(`ads`가 그보다 클 때) 통과한다.
 * 횟수만 세는 판정이라 새로고침·재시도로 광고를 피할 수 없다.
 *
 * 해석이 실패해 환불되면 `used`만 줄고 `ads`는 남는다 → 다음 한 번은 광고 없이 지나간다.
 * 이미 본 광고를 버리지 않기 위해서다.
 */
export const needsAdFor = (used: number, ads: number): boolean =>
  used >= FREE_LIMIT && ads <= used - FREE_LIMIT;

export interface QuotaStatus {
  /** 오늘 쓴 횟수 */
  used: number;
  /** 광고 없이 볼 수 있는 무료 한도 */
  freeLimit: number;
  /** 남은 무료 횟수 */
  freeRemaining: number;
  /** 하루 최대(광고 구간 포함) */
  hardLimit: number;
  /** 하루 상한까지 남은 횟수 */
  remaining: number;
  isMember: boolean;
  /** 오늘 시청한 광고 수 */
  adsWatched: number;
  /** 다음 해석에 광고가 필요한지 */
  needsAd: boolean;
  /** 초기화 시각(epoch ms) — 한국시간 자정 */
  resetsAt: number;
}

/**
 * 비회원 리딩 보관 기간 — 이 시간이 지나면 정리 배치가 지운다 (apps/api lib/cleanup.ts).
 *
 * 해석이 끝나면 공유 링크를 미리 발행하는데(iOS 공유 시트 제약), 그 탓에 공유하지 않은
 * 해석까지 남는다. 비회원 것은 그 자리에서 공유하려고 만든 링크라 하루만 지나도 쓸모가 없다.
 * 회원 것은 지난 타로(히스토리)이므로 지우지 않는다.
 */
export const GUEST_READING_TTL_MS = 2 * 24 * 60 * 60 * 1000;

/**
 * 짧은 구간 폭주로 막혔을 때 — 한도 소진이 아니라 "속도"가 문제라는 걸 분명히 한다.
 * 정상 이용자가 이 문구를 볼 일은 거의 없고, 봐도 잠시 뒤 그대로 진행된다(횟수 차감 없음).
 */
export const TOO_FAST_MESSAGE = '요청이 너무 빨라요. 잠시 후 다시 시도해 주세요.';

/** 전역 일일 상한에 걸렸을 때 — 개인 한도와 구분되는 안내 */
export const SERVICE_BUSY_MESSAGE =
  '오늘 무료로 제공할 수 있는 해석을 모두 사용했어요. 내일 다시 만나요 🌙';

/** 하루 상한 도달 안내 — 로그인해도 횟수는 같으므로 로그인을 권하지 않는다 */
export function quotaExceededMessage(s: Pick<QuotaStatus, 'hardLimit'>): string {
  return `오늘 볼 수 있는 ${s.hardLimit}번을 모두 사용했어요. 내일 다시 만나요.`;
}
